import React, { useMemo } from "react";
import { OverLayParameter } from "../../../App";
import { RequirementsProps } from "../../../tools/context";
import { useTranslation } from "../../../tools/i18n";
import Modal from "../../Modal";
import { Renderer as ShopRenderer } from "../../Shop";

interface SpotDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  spot?: any;
  requirements: RequirementsProps;
  setOverlay?: (p: OverLayParameter) => void;
  siteConfiguration?: any;
  categories?: any;
}

const HARDCODED_SHOP_CONFIG = {
  hideShortcode: true,
  hideBookButton: true,
  hideReview: true,
  hideMail: false,
  hidePhone: false,
  hidePrice: false,
  enablePromoTracking: false,
  disableGiftCardHighlight: true,
  advantagesRedirectToWallet: false,
};

const buildComponentStates = (spot: any) => {
  if (!spot) return null;

  const lat = spot.latitude ?? spot.lat;
  const lng = spot.longitude ?? spot.lng;
  const city =
    spot.city ||
    spot.dept ||
    (typeof spot.formatted_address === "string"
      ? spot.formatted_address.split(",").pop()?.trim()
      : "");

  const seller = {
    id: spot.id,
    name: spot.name || spot.store_name || "",
    frontCateg: spot.frontCateg || spot.tca_categ || spot.category || "",
    latitude: lat ? String(lat) : undefined,
    longitude: lng ? String(lng) : undefined,
    contactEmail: spot.contactEmail || spot.email || "",
    contactPhone: spot.contactPhone || spot.phone || "",
    shortcode: spot.shortcode || "",
  };

  const images: string[] = Array.isArray(spot.images) && spot.images.length
    ? spot.images.filter(Boolean)
    : spot.urlimg
      ? [spot.urlimg]
      : [];

  const data = {
    description: spot.description || spot.post_content || "",
    description_en: spot.description_en || spot.post_content_en || "",
    description_es: spot.description_es || spot.post_content_es || "",
    description_de: spot.description_de || spot.post_content_de || "",
    description_it: spot.description_it || spot.post_content_it || "",
    description_nl: spot.description_nl || spot.post_content_nl || "",
    street: spot.street || spot.adr || "",
    zipcode: spot.zipcode || spot.dept_nb || "",
    city: city || "",
    formattedAddress: spot.formatted_address || spot.adr || "",
    website: spot.website || spot.url || "",
    photo_credit: spot.photo_credit || "",
    opening_hours: spot.opening_hours || null,
    price_level: spot.price_level || null,
    booking_url: spot.booking_url || "",
    google_grade: spot.google_grade,
    google_review: spot.google_review,
    seller_type: spot.seller_type || (spot.type === "site" ? "store" : ""),
    hebergement_accessible_france: spot.hebergement_accessible_france,
    assigned_disabilities: spot.assigned_disabilities,
    quality: spot.quality,
  };

  return {
    seller,
    data,
    images,
    review: [],
    coupons: [],
  };
};

const SpotDetailModal = ({
  isOpen,
  onClose,
  spot,
  requirements,
  setOverlay,
  siteConfiguration = {},
  categories = {},
}: SpotDetailModalProps) => {
  const { t } = useTranslation();
  const componentStates = useMemo(() => buildComponentStates(spot), [spot]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="SpotDetailModal">
      <div className="SpotDetailModal__body">
        {!componentStates?.seller ? (
          <div className="SpotDetailModal__empty">
            <p>{t("travelPlaner.Travel.knowMore")}</p>
          </div>
        ) : (
          <ShopRenderer
            componentStates={componentStates}
            requirements={requirements}
            navigation={null}
            headerButtons={null}
            setOverlay={setOverlay ?? (() => undefined)}
            siteConfiguration={siteConfiguration}
            categories={categories}
            giftCardText={{
              title: "",
              titleCoupon: "",
              subTitle: "",
              label: "",
              button: "",
              logo: "",
            }}
            reviewTitle=""
            {...HARDCODED_SHOP_CONFIG}
          />
        )}
      </div>
    </Modal>
  );
};

export default SpotDetailModal;
